/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { css, StyleSheet } from 'aphrodite';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { colors, mixins, LoadingSpinner } from '@blend/consumer-components';
import { BlIcon, ProvideTimers, TimerProps } from '@blend/shared-react';

import { DocumentsAPI } from '../../../api/common/documents';
import { notaryAPI } from '../../../api/notary';
import { setCurrentDocument } from '../../../actions/documentActions';
import SigningDocument from '../../common/Documents/SigningDocument';
import { history } from '../../../customHistory';
import { RouterStates } from '../../../definitions/RouterStates';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { Document } from './Document';
import { DraggableDocumentWrapper } from './DraggableDocumentWrapper';
import { partition, concat, size } from '@utils/lodash';
import { DocumentStatus } from '@shared/constants/document';
export namespace Documents {
  export interface OwnProps {
    ownerId: string;
    documents: DocumentsAPI.DocumentsResponse | null;
    setDocuments: (docs: DocumentsAPI.DocumentsResponse) => void;
    fetchDocuments: () => void;
    onSelectDocument: (doc: SigningDocument) => void;
    setIsUploaderOpen: (show: boolean) => void;
  }

  export type Props = OwnProps & TimerProps;
}

const styles = StyleSheet.create({
  wrapper: {
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
    ':last-child': {
      borderBottom: 'none',
    },
  },
  headingWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: colors.catalystLight01,
    ...mixins.padding(8),
    ...mixins.borderBottom(1, 'solid', mixins.globalBorderColor),
    ':first-child': {
      ...mixins.borderTop(1, 'solid', mixins.globalBorderColor),
    },
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addIcon: {
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    ...mixins.margin(0, 10, 0, 0),
    ...mixins.padding(0),
  },
});

const documentRefreshInterval = 1500;

export const Documents: React.FC<Documents.Props> = ({
  ownerId,
  documents,
  setDocuments,
  fetchDocuments,
  onSelectDocument,
  setIsUploaderOpen,
  reactSafeSetInterval,
}) => {
  const dispatch = useDispatch();
  const { currentDoc: currentDocState } = useRootSelector(state => state.document);
  const [isPaused, setPaused] = useState(false);
  const [currentDocId, setCurrentDocId] = useState<string | null>(null);

  const showLoading = !documents;
  const [docPackage, discarded] = partition(documents, doc => doc.status !== DocumentStatus.DISCARDED);

  useEffect(() => {
    // Fetching documents and updating document order can conflict
    // fetch documents is paused until dragging is complete
    const interval = reactSafeSetInterval(async () => {
      if (!isPaused) {
        try {
          // eslint-disable-next-line @typescript-eslint/await-thenable
          await fetchDocuments();
        } catch (e) {
          const status = (e as any)?.response?.status;
          // If the user is not authorized to view the documents, redirect to the pipeline
          if (status === 403) {
            history.push(RouterStates.ROOMS);
            return;
          }
        }
      }
    }, documentRefreshInterval);
    return () => clearInterval(interval);
  }, [isPaused, fetchDocuments, reactSafeSetInterval]);

  useEffect(() => {
    const foundDoc = documents?.find(document => document.id === currentDocId);
    const shouldUpdateCurrentDoc = foundDoc !== undefined && foundDoc.status !== currentDocState?.status;
    if (shouldUpdateCurrentDoc) {
      setCurrentDocument(foundDoc)(dispatch);
    }
  }, [dispatch, documents, currentDocId, currentDocState?.status]);

  const handleDocumentClick = (document: SigningDocument) => {
    setCurrentDocId(document.id);
    setCurrentDocument(document)(dispatch);
    onSelectDocument(document);
  };

  // Orders the active documents whenever a document is moved
  const reorder = (list: SigningDocument[], startIndex: number, endIndex: number): SigningDocument[] => {
    const result = list;
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = async (movedDoc: DropResult) => {
    // If moved document doesn't move to a valid location or is returned to its original location
    if (!movedDoc.destination || movedDoc.destination.index === movedDoc.source.index) {
      return;
    }

    const reorderedDocuments = reorder(docPackage, movedDoc.source.index, movedDoc.destination.index);
    setDocuments(concat(reorderedDocuments, discarded));

    // puts the reordered documents' ids into a list
    const docIdOrder: string[] = reorderedDocuments.map(d => d.id);

    await notaryAPI.documents.orderDocuments(ownerId, docIdOrder);
  };

  return (
    <div className={css(styles.wrapper)} data-it="Documents">
      {showLoading ? (
        <div className={css(styles.loadingWrapper)}>
          <LoadingSpinner size={'sm'} color={colors.catalyst} />
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <>
            <div className={css(styles.headingWrapper)} data-it="document-package">
              <div>
                Document Package (<span data-it="document-package-count">{size(docPackage)}</span>)
              </div>
              <button
                data-it="AddDocumentButton"
                className={css(styles.addIcon)}
                onClick={() => {
                  setIsUploaderOpen(true);
                }}
              >
                <BlIcon icon="plus" size="sm" />
              </button>
            </div>
            <Droppable droppableId="doc-package-area">
              {dropProvided => (
                <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                  {docPackage.map((d, i) => (
                    <Draggable key={`document-${d.id}`} draggableId={`document-${d.id}`} index={i}>
                      {(dragProvided, dragSnapshot) => (
                        <DraggableDocumentWrapper
                          setPaused={setPaused}
                          dragProvided={dragProvided}
                          dragSnapshot={dragSnapshot}
                          index={i}
                        >
                          <Document
                            isTrash={false}
                            isSelected={!!currentDocState && currentDocState.id === d.id}
                            key={d.id}
                            document={d}
                            handleClick={handleDocumentClick}
                          />
                        </DraggableDocumentWrapper>
                      )}
                    </Draggable>
                  ))}
                  {/* Placeholder leaves a space open when dragging documents for easy visibility */}
                  {dropProvided.placeholder}
                </div>
              )}
            </Droppable>
            <span className={css(styles.headingWrapper)} data-it={'trash'}>
              Trash ({size(discarded)})
            </span>
            {discarded.map(d => (
              <Document
                isTrash={true}
                isSelected={!!currentDocState && currentDocState.id === d.id}
                key={`${d.id}`}
                document={d}
                handleClick={handleDocumentClick}
              />
            ))}
          </>
        </DragDropContext>
      )}
    </div>
  );
};

export default (props: any) => <ProvideTimers render={timerProps => <Documents {...props} {...timerProps} />} />;
